<template>
  <!-- Modal Principal -->
  <Teleport to="body">
    <div v-if="modelValue" class="modal-backdrop" :style="{ zIndex: zIndexEfetivo }" @click.self="fechar">
      <div class="modal-container shadow modal-lg" role="dialog" aria-modal="true">
        <header class="modal-header">
          <h5 class="modal-title">Assistente de Preenchimento (IA)</h5>
          <button type="button" class="btn-close btn-close-white" @click="fechar" aria-label="Close"></button>
        </header>

        <div class="modal-body">
          <div class="assistente-container">
            <div class="alert alert-info mb-3">
              <i class="bi bi-info-circle me-2"></i>
              Cole abaixo o conteúdo do Packing List ou Container (texto, tabelas do Excel ou imagens).
            </div>

            <div
              class="paste-area form-control mb-3"
              contenteditable="true"
              @paste="handlePaste"
              ref="pasteArea"
              placeholder="Cole aqui (Ctrl+V)..."
            ></div>

            <div v-if="pastedImages.length > 0" class="images-preview mb-3">
              <div v-for="(img, index) in pastedImages" :key="index" class="image-thumbnail">
                <img :src="img" alt="Pasted image" />
                <button type="button" class="btn-close btn-close-white remove-img" @click="removeImage(index)"></button>
              </div>
            </div>

            <div class="actions-container d-flex gap-2 justify-content-center mt-4">
              <button
                v-if="modo === 'completo'"
                type="button"
                class="btn btn-primary"
                @click="extrairTudo"
                :disabled="isLoading || (!textContent && pastedImages.length === 0)"
              >
                <span v-if="activeAction === 'extrairTudo'" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-stars me-2"></i>
                Extrair tudo (Packing List + Containers)
              </button>
              <button
                v-if="modo === 'completo' || modo === 'packing-list'"
                type="button"
                class="btn btn-outline-primary"
                @click="extrairPackingList"
                :disabled="isLoading || (!textContent && pastedImages.length === 0)"
              >
                <span v-if="activeAction === 'extrairPackingList'" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-magic me-2"></i>
                Extrair só Packing List
              </button>
              <button
                v-if="modo === 'completo' || modo === 'containers'"
                type="button"
                class="btn btn-outline-success"
                @click="extrairContainers"
                :disabled="isLoading || (!textContent && pastedImages.length === 0)"
              >
                <span v-if="activeAction === 'extrairContainers'" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-truck me-2"></i>
                Extrair só Containers
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Modal de Aguardo (não fechável) -->
  <ModalAguardandoIA
    v-model="showModalAguardando"
    :tentativa-atual="retryAttempt"
    :total-tentativas="MAX_RETRIES"
  />

  <!-- Modal de Indisponibilidade (fechável) -->
  <ModalIAIndisponivel
    v-model="showModalIndisponivel"
    :contatar-suporte="contatarSuporte"
    :total-tentativas="MAX_RETRIES"
  />
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import ModalAguardandoIA from '@/components/base/modal/ModalAguardandoIA.vue'
import ModalIAIndisponivel from '@/components/base/modal/ModalIAIndisponivel.vue'
import api from '@/services/api'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  modelValue: Boolean,
  idEmbarque: { type: Number, default: 0 },
  modo: { type: String, default: 'completo' } // 'completo' | 'packing-list' | 'containers'
})

const emit = defineEmits(['update:modelValue', 'packing-list-salvo', 'containers-salvo', 'tudo-salvo', 'show-notification'])

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

// === Configuração de Retry ===
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 8000
const STORAGE_KEY = 'comex_ia_fail_count'
const MAX_FAILS_BEFORE_SUPPORT = 3

// === Estado do Assistente ===
const isLoading = ref(false)
const activeAction = ref(null) // 'extrairTudo' | 'extrairPackingList' | 'extrairContainers' | null
const pasteArea = ref(null)
const textContent = ref('')
const pastedImages = ref([])

// === Estado do Retry ===
const isRetrying = ref(false)
const retryAttempt = ref(0)
const showModalAguardando = ref(false)
const showModalIndisponivel = ref(false)
const contatarSuporte = ref(false)

// === Controle de falhas acumuladas (localStorage) ===
function getFailCount () {
  return parseInt(localStorage.getItem(STORAGE_KEY) || '0')
}

function incrementFailCount () {
  const count = getFailCount() + 1
  localStorage.setItem(STORAGE_KEY, count.toString())
  return count
}

function resetFailCount () {
  localStorage.setItem(STORAGE_KEY, '0')
}

// === Watch para limpar ao abrir o modal ===
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
    textContent.value = ''
    pastedImages.value = []
    activeAction.value = null
    isLoading.value = false
    if (pasteArea.value) {
      pasteArea.value.innerHTML = ''
    }
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

// === Paste handler ===
const handlePaste = (e) => {
  e.preventDefault()

  // Handle text
  const text = e.clipboardData.getData('text/plain')
  if (text) {
    textContent.value += text + '\n'
    const textNode = document.createTextNode(text)
    pasteArea.value.appendChild(textNode)
    pasteArea.value.appendChild(document.createElement('br'))
  }

  // Handle images
  const items = e.clipboardData.items
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf('image') !== -1) {
      const blob = items[i].getAsFile()
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64 = event.target.result
        pastedImages.value.push(base64)

        const img = document.createElement('img')
        img.src = base64
        img.style.maxHeight = '100px'
        img.style.margin = '5px'
        pasteArea.value.appendChild(img)
      }
      reader.readAsDataURL(blob)
    }
  }
}

const removeImage = (index) => {
  pastedImages.value.splice(index, 1)
}

// === Método genérico de extração com retry ===
const extrairComRetry = async (actionName, endpoint, successEvent, successMessageFn) => {
  if (!props.idEmbarque) {
    emit('show-notification', { message: 'Salve o embarque primeiro antes de usar o Assistente IA.', type: 'warning' })
    return
  }

  isLoading.value = true
  activeAction.value = actionName
  isRetrying.value = false
  retryAttempt.value = 0
  showModalAguardando.value = false
  showModalIndisponivel.value = false

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    retryAttempt.value = attempt

    const payload = {
      idEmbarque: props.idEmbarque,
      textContent: textContent.value,
      base64Images: pastedImages.value,
      attempt: attempt
    }

    try {
      const res = await api.post(endpoint, payload)

      // === SUCESSO ===
      isLoading.value = false
      activeAction.value = null
      isRetrying.value = false
      showModalAguardando.value = false
      resetFailCount()

      const temDados = Array.isArray(res.data)
        ? res.data.length > 0
        : (res.data?.packingLists?.length > 0 || res.data?.containers?.length > 0)

      if (res.data && temDados) {
        emit(successEvent)
        emit('show-notification', {
          message: successMessageFn(res.data),
          type: 'success'
        })
      } else {
        emit('show-notification', {
          message: 'Nenhum dado extraído.',
          type: 'warning'
        })
      }

      fechar()
      return
    } catch (e) {
      const status = e.response?.status
      // Códigos retryáveis: 429 TooManyRequests, 5xx (erro servidor), 0 (sem conexão) ou sem resposta (timeout/network error)
      const isRetryable = !e.response || status === 429 || (status >= 500 && status < 600) || status === 0

      if (isRetryable && attempt < MAX_RETRIES) {
        // Mostra modal de aguardo após a primeira falha
        if (!isRetrying.value) {
          isRetrying.value = true
          showModalAguardando.value = true
        }
        // Atualiza a tentativa para a próxima antes de aguardar para exibir corretamente no modal
        retryAttempt.value = attempt + 1
        // Aguarda 8 segundos antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
      } else {
        // === FALHA FINAL (todas as tentativas esgotadas) ===
        isLoading.value = false
        activeAction.value = null
        isRetrying.value = false
        showModalAguardando.value = false

        // Incrementa contador de falhas acumuladas
        const totalFails = incrementFailCount()
        contatarSuporte.value = totalFails > MAX_FAILS_BEFORE_SUPPORT

        // Exibe modal de indisponibilidade
        showModalIndisponivel.value = true

        console.error(`Erro ao extrair via IA após ${MAX_RETRIES} tentativas:`, e)
        return
      }
    }
  }
}

const extrairPackingList = () => {
  extrairComRetry(
    'extrairPackingList',
    '/EmbarquesAi/extrair-packing-list',
    'packing-list-salvo',
    (data) => `${data.length} lotes extraídos e salvos com sucesso!`
  )
}

const extrairContainers = () => {
  extrairComRetry(
    'extrairContainers',
    '/EmbarquesAi/extrair-containers',
    'containers-salvo',
    (data) => `${data.length} container(es) extraído(s) e salvo(s) com sucesso!`
  )
}

const extrairTudo = () => {
  extrairComRetry(
    'extrairTudo',
    '/EmbarquesAi/extrair-tudo',
    'tudo-salvo',
    (data) => {
      const total = (data?.packingLists?.length || 0) + (data?.containers?.length || 0)
      return `${total} itens extraídos e salvos com sucesso!`
    }
  )
}

const fechar = () => {
  isLoading.value = false
  activeAction.value = null
  emit('update:modelValue', false)
}
</script>

<style scoped>
/* ===== Estrutura do Modal (copiada de ModalFormulario.vue) ===== */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-container {
  width: 90%;
  max-width: 800px;
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.modal-container.modal-lg {
  max-width: 800px;
}

.modal-header {
  padding: 1rem 1.5rem;
  background-color: #34495E;
  color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.paste-area {
  min-height: 150px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px dashed var(--bs-border-color);
  padding: 10px;
}
.paste-area:empty:before {
  content: attr(placeholder);
  color: var(--bs-secondary-color);
  pointer-events: none;
  display: block; /* For Firefox */
}
.images-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.image-thumbnail {
  position: relative;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 2px;
}
.image-thumbnail img {
  max-height: 100px;
  border-radius: 2px;
}
.remove-img {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: var(--bs-danger);
  border-radius: 50%;
  padding: 4px;
  font-size: 0.5rem;
}
</style>
